import { Loader } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { LoginSignupPanel } from 'src/pages/LoginSignup/LoginSignupPanel'
import { SignupSuccessPanel } from 'src/pages/SignupSuccess/SignupSuccessPanel'
import { Column } from 'src/shared/components/Semantic/all'
import { PageContainer } from 'src/shared/components/PageContainer'
import { Dialog, DialogContent } from 'src/shared/components/ui/dialog'
import { useAuth } from 'src/shared/hooks/authProvider'
import { TEMP_AGENT_FORCE_LOGGED_IN } from 'src/shared/hooks/tempAgentAuthBypass'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'

export const MyAccountAnonAuthGate = ({
  children,
}: {
  children: ReactNode
}) => {
  const { loading: authLoading, session } = useAuth()
  const { orgUser } = useOrgUsersApi()
  const { organization } = useOrgsApi({})
  const isAdmin = useIsAdmin()

  const orgUserReady = !session?.user?.id || orgUser !== undefined
  const hasOrgId = !!orgUser?.data?.organization_id
  const orgDetailsPending = hasOrgId && organization === undefined

  const orgRow = organization?.data ?? null

  const [signupWizardOrgId, setSignupWizardOrgId] = useState<string | null>(
    null
  )
  const [step, setStep] = useState<'login-signup' | 'signup-success'>(
    'login-signup'
  )
  const [isLoginMode, setIsLoginMode] = useState(false)

  const isAnonOrg = orgRow?.name === 'Anon'
  const showGate =
    !TEMP_AGENT_FORCE_LOGGED_IN &&
    !import.meta.env.DEV &&
    !isAdmin &&
    (isAnonOrg || signupWizardOrgId !== null)

  if (
    !import.meta.env.DEV &&
    (authLoading || !orgUserReady || orgDetailsPending)
  ) {
    return (
      <div className="w-full min-h-[40vh] flex items-center justify-center">
        <Loader className="animate-spin" />
      </div>
    )
  }

  return (
    <>
      {children}

      <Dialog
        open={showGate}
        onOpenChange={(next) => {
          if (next) return
        }}
      >
        <DialogContent
          showClose={false}
          className="left-[50%] top-[50%] z-[200] h-[80vh] w-[80vw] max-h-[80vh] max-w-[80vw] translate-x-[-50%] translate-y-[-50%] gap-0 border-0 p-0 flex flex-col overflow-hidden rounded-xl"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <Column className="flex h-full w-full min-h-0 min-w-0 flex-col overflow-hidden bg-background rounded-xl">
            {step === 'login-signup' ? (
              <PageContainer width="xl" className="min-h-0 flex-1 overflow-y-auto">
                <LoginSignupPanel
                  loginModeSource="controlled"
                  isLoginMode={isLoginMode}
                  onLoginModeChange={setIsLoginMode}
                  onSignupOrgReady={(orgId) => {
                    setSignupWizardOrgId(orgId)
                    setStep('signup-success')
                  }}
                  className="h-full min-h-[min(100%,520px)]"
                />
              </PageContainer>
            ) : (
              <Column className="min-h-0 flex-1 overflow-hidden">
                <SignupSuccessPanel
                  orgId={signupWizardOrgId}
                  variant="embedded"
                  onSuccess={() => {
                    setSignupWizardOrgId(null)
                    setStep('login-signup')
                  }}
                />
              </Column>
            )}
          </Column>
        </DialogContent>
      </Dialog>
    </>
  )
}
